/**
 * Фильтры экрана «Поля» живут в АДРЕСЕ, а не в локальном состоянии (AK-750).
 *
 * Сценарий founder 25.08: «отфильтровал нужные хозяйства → открыл поле →
 * нажал назад → фильтры сохраняются». Локальный `useState` этого не умеет
 * ни при возврате, ни при перезагрузке, ни при пересылке ссылки коллеге —
 * поэтому источник правды один и он в URL (тот же канон, что у обзора СПП:
 * `?level=&id=`, ревью head-web #892 п.20).
 *
 * ⚠️ Грабля TanStack (AK-591): search (де)сериализуется через JSON, поэтому
 * СТРОКА `'true'` уехала бы в адрес как `has_irrigation=%22true%22` и
 * рукописный `?has_irrigation=true` (а это то, что человек напишет, когда
 * будет править ссылку руками) прочитался бы как булево и не совпал бы со
 * строкой. Поэтому орошение живёт БУЛЕВЫМ: `true`/`false` round-trip'ятся
 * без кавычек. На вход всё равно принимаем и строку — ссылки из переписки
 * приходят разные.
 */

/** Фильтры списка полей в адресе. Пустой фильтр = отсутствующий параметр. */
export interface FieldsListSearch {
  /** Поиск по названию поля. */
  search?: string;
  cluster_id?: string;
  farm_id?: string;
  brigade_id?: string;
  /** Код статуса занятия (`sowings.status`), см. `lib/sowings/statusFilter`. */
  sowing_status?: string;
  /** Культура — свободный текст, как его понимает бэкенд списка. */
  crop?: string;
  has_irrigation?: boolean;
  /**
   * Режим «Архив» — показываем ТОЛЬКО архивные поля (AK-750).
   *
   * Числом `1`, а не строкой и не булевым: TanStack (де)сериализует search
   * через JSON, и строка `'1'` уехала бы в адрес как `%221%22` (грабля
   * AK-591, та же, что на карте с `?addField=1`). Булево тоже сработало бы,
   * но флаги экранов в этом проекте уже живут числом — держим один приём.
   */
  archived?: 1;
  /**
   * «Вся компания» выбрана ЯВНО (AK-772).
   *
   * Нужен отдельный флаг, потому что у экрана три разных состояния, а не два:
   * хозяйство в адресе, «в адресе пусто» и «человек осознанно попросил всю
   * компанию». Пусто = «фильтра нет» → экран берёт умолчание из области,
   * выбранной в правой панели (гибрид). Без флага «вся компания» было бы
   * неотличимо от «пусто» и через мгновение схлопывалось бы обратно в
   * хозяйство панели — то есть выбор человека молча отменялся бы.
   *
   * Числом `1` по тому же уговору, что `archived` (грабля JSON в адресе).
   */
  whole_company?: 1;
}

/** Ключи фильтров — один список на валидатор, каскад и очистку. */
const KEYS = [
  'search',
  'cluster_id',
  'farm_id',
  'brigade_id',
  'sowing_status',
  'crop',
  'has_irrigation',
  'archived',
  'whole_company',
] as const;

function str(v: unknown): string | undefined {
  // Не `trim()`: человек в поиске набирает «поле 1», и промежуточное «поле »
  // обязано остаться тем, что он набрал. Пустая строка — это «фильтра нет».
  return typeof v === 'string' && v !== '' ? v : undefined;
}

/** Флаг-переключатель экрана: живёт числом 1, `undefined` = выключен. */
function flag(v: unknown): 1 | undefined {
  return v === 1 || v === '1' || v === true ? 1 : undefined;
}

function bool(v: unknown): boolean | undefined {
  if (v === true || v === 'true') return true;
  if (v === false || v === 'false') return false;
  return undefined;
}

/**
 * `validateSearch` роута `/fields`.
 *
 * Всё, чего нет в этом списке, TanStack Router вырежет из адреса — поэтому
 * список обязан содержать ВСЕ фильтры экрана (иначе фильтр «работает, пока
 * не нажмёшь назад»).
 */
export function fieldsListSearch(raw: Record<string, unknown>): FieldsListSearch {
  return {
    search: str(raw.search),
    cluster_id: str(raw.cluster_id),
    farm_id: str(raw.farm_id),
    brigade_id: str(raw.brigade_id),
    sowing_status: str(raw.sowing_status),
    crop: str(raw.crop),
    has_irrigation: bool(raw.has_irrigation),
    archived: flag(raw.archived),
    whole_company: flag(raw.whole_company),
  };
}

/**
 * Новое состояние адреса после правки одного фильтра.
 *
 * Здесь же каскад справочников: сменил кластер — прежнее хозяйство ему уже
 * может не принадлежать, сменил хозяйство — прежнее отделение тем более.
 * Каскад живёт в чистой функции, а не в обработчике выпадашки, потому что
 * иначе он выполняется ДВУМЯ навигациями подряд, и вторая затирает первую.
 *
 * Пустое значение (`undefined` / `''`) = «фильтра нет»: ключ уходит из
 * адреса, а не остаётся пустым хвостом `?crop=`.
 */
export function nextFieldsSearch(
  prev: FieldsListSearch,
  patch: Partial<FieldsListSearch>,
): FieldsListSearch {
  const merged: Record<string, unknown> = { ...prev, ...patch };
  if ('cluster_id' in patch) {
    if (!('farm_id' in patch)) merged.farm_id = undefined;
    if (!('brigade_id' in patch)) merged.brigade_id = undefined;
  }
  if ('farm_id' in patch && !('brigade_id' in patch)) {
    merged.brigade_id = undefined;
  }
  // Область экрана — ОДНА: хозяйство, кластер или «вся компания». Флаг и
  // сужение не могут стоять в адресе вместе, иначе ссылка описывает два
  // разных фильтра сразу и читатель гадает, какой победил.
  if (patch.whole_company) {
    if (!('farm_id' in patch)) merged.farm_id = undefined;
    if (!('cluster_id' in patch)) merged.cluster_id = undefined;
    if (!('brigade_id' in patch)) merged.brigade_id = undefined;
  }
  if ((patch.farm_id || patch.cluster_id) && !('whole_company' in patch)) {
    merged.whole_company = undefined;
  }
  const next = fieldsListSearch(merged);
  // Ключи с `undefined` роутер выбрасывает при сериализации сам, но чистим
  // и здесь: объект сравнивается в тестах и в `resetKey` таблицы.
  for (const key of KEYS) {
    if (next[key] === undefined) delete next[key];
  }
  return next;
}

/** Сужен ли список хоть чем-нибудь. */
export function hasFieldsFilters(s: FieldsListSearch): boolean {
  return KEYS.some((k) => s[k] !== undefined);
}

/**
 * Параметры запроса списка полей — ровно из адреса, без второго источника.
 *
 * Держим рядом с валидатором намеренно: «что в адресе» и «что уходит на
 * сервер» обязаны меняться одной правкой, иначе фильтр в URL есть, а в
 * выборке его нет.
 */
export function fieldsQueryParams(
  s: FieldsListSearch,
  opts: {
    limit: number;
    offset: number;
    /** Тянуть ли `slope_mean_deg` (колонка «Уклон» видна). */
    wantTerrain?: boolean;
    sort?: { field: string; order: 'asc' | 'desc' } | null;
  },
): URLSearchParams {
  const qs = new URLSearchParams();
  if (s.search) qs.set('search', s.search);
  if (s.farm_id) qs.set('farm_id', s.farm_id);
  if (s.cluster_id) qs.set('cluster_id', s.cluster_id);
  if (s.brigade_id) qs.set('brigade_id', s.brigade_id);
  if (s.sowing_status) qs.set('sowing_status', s.sowing_status);
  if (s.crop && s.crop.trim()) qs.set('crop', s.crop.trim());
  if (s.has_irrigation !== undefined) {
    qs.set('has_irrigation', String(s.has_irrigation));
  }
  // Режим «Архив» — отдельный флаг бэкенда: не «живые + архивные», а только
  // архивные (иначе десятки архивных дублей теряются среди тысяч живых).
  if (s.archived) qs.set('archived_only', 'true');
  if (opts.wantTerrain) qs.set('include', 'terrain');
  if (opts.sort) {
    qs.set('sort', opts.sort.field);
    qs.set('order', opts.sort.order);
  }
  qs.set('limit', String(opts.limit));
  qs.set('offset', String(opts.offset));
  return qs;
}

/**
 * Параметры счётчиков статусов занятия (AK-711): те же фильтры МИНУС сам
 * статус — иначе, выбрав один пункт, теряешь из выпадашки остальные.
 */
export function fieldsFacetParams(s: FieldsListSearch): URLSearchParams {
  const qs = fieldsQueryParams({ ...s, sowing_status: undefined }, {
    limit: 0,
    offset: 0,
  });
  qs.delete('limit');
  qs.delete('offset');
  return qs;
}
